import { PersonalizationMap } from '@/stores/main'

import { sortBy } from 'lodash-es'

import { getTextPersonalized } from '@/lib/personalization'

import { CartItemCustomization, PromptType, PromptValuePair } from '@/types/CartItemCustomization'
import { LocalCartItemCustomization } from '@/types/LocalCartItemCustomization'

import type {
  CartItemStorefrontDto,
  DropdownPromptDto,
  ImagePromptDto,
  ImagePromptResponseDto,
  MediaReferenceDto,
  PromptDto,
  PromptResponseDto,
  TextPromptDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

const constructCartBundleItemPreviewUrls = (cartItem: CartItemStorefrontDto): string[] | null => {
  if (
    cartItem.item?.canvasLayout === null ||
    cartItem.cartItemCustomizationDetails.promptResponses.length === 0
  ) {
    return null
  }

  if (
    cartItem.item?.canvasLayout.canvasSideDetails !== null &&
    cartItem.item?.canvasLayout.canvasSideDetails !== undefined &&
    cartItem.item?.canvasLayout.canvasSideDetails?.sides.length > 0
  ) {
    const sideUrls: string[] = cartItem.item.canvasLayout.canvasSideDetails.sides.map((side) => {
      const promptsQuerystring =
        cartItem.cartItemCustomizationDetails.promptResponses
          .map((promptResponse: Partial<PromptResponseDto> | Partial<ImagePromptResponseDto>) => {
            if (typeof promptResponse.value === 'string') {
              return getPromptTextUrl(promptResponse)
            } else {
              return getPromptImageUrl(promptResponse)
            }
          })
          .join('&') + (side.canvasId ? `&canvasId=${encodeURIComponent(side.canvasId)}` : '')

      return `${import.meta.env.VITE_SUNNY_16_RENDER_API_BASE_URL}${cartItem.item?.canvasLayout?.assetName}?${promptsQuerystring}`
    })

    return sideUrls
  }

  const promptsQuerystring = cartItem.cartItemCustomizationDetails.promptResponses
    .map((promptResponse: Partial<PromptResponseDto> | Partial<ImagePromptResponseDto>) => {
      if (typeof promptResponse.value === 'string') {
        return getPromptTextUrl(promptResponse)
      } else {
        return getPromptImageUrl(promptResponse)
      }
    })
    .join('&')

  return [
    `${import.meta.env.VITE_SUNNY_16_RENDER_API_BASE_URL}${cartItem.item?.canvasLayout.assetName}?${promptsQuerystring}`
  ]
}

const constructLivePreviewUrl = (
  cartItem: CartItemStorefrontDto,
  itemCustomizations: CartItemCustomization[],
  overridePromptValues: LocalCartItemCustomization[],
  canvasId?: string
): string | null => {
  // If there are no customizations, we don't need to construct a URL
  if (cartItem.item?.canvasLayout === null || overridePromptValues.length === 0) {
    return null
  }

  // Helper function to get prompt URL segment based on prompt type
  const getPromptUrl = (
    promptName: string,
    promptType: PromptType,
    overridePromptValue: string | MediaReferenceDto
  ): string | null => {
    if (promptType === PromptType.TEXT) {
      return `${promptName}=${encodeURIComponent(overridePromptValue as string)}`
    } else if (promptType === PromptType.IMAGE) {
      const mediaRef = overridePromptValue as MediaReferenceDto
      let imagePromptUrl = `${promptName}.imageUrl=${encodeURIComponent(mediaRef.thumbUrl)}`

      if (mediaRef.attributes && mediaRef.attributes.length > 0) {
        imagePromptUrl += `&${promptName}.attributes=${encodeURIComponent(mediaRef.attributes)}`
      }

      return imagePromptUrl
    }
    return null
  }

  // If there are customizations, we need to construct a URL
  const promptsQuerystring =
    itemCustomizations
      .flatMap((itemCustomization) => {
        // Find local prompts for the current cart item
        const overridePromptsForCartItem = overridePromptValues.filter((overridePrompt) =>
          overridePrompt.cartItemIds.includes(cartItem.cartItemId)
        )

        if (itemCustomization.isSingleItemCustomization()) {
          if (itemCustomization.singleItemCustomization?.cartItemId === cartItem.cartItemId) {
            return (
              itemCustomization.singleItemCustomization?.promptValuePairs.map((promptValuePair) => {
                // We're not using the promptValuePair.value here because we want to use the local value
                const overridePrompt = overridePromptsForCartItem.find(
                  (overridePrompt) => overridePrompt.promptId === promptValuePair.promptId
                )

                if (!overridePrompt || !overridePrompt.value) {
                  return null
                }

                return getPromptUrl(
                  promptValuePair.promptName,
                  promptValuePair.promptType,
                  overridePrompt.value
                )
              }) || []
            )
          }
        } else if (itemCustomization.isMultiItemCustomization()) {
          return (
            itemCustomization.multiItemCustomization?.cartItemValuePairs.map(
              (cartItemValuePair) => {
                // We're not using the cartItemValuePair.value here because we want to use the local value
                const overridePrompt = overridePromptsForCartItem.find((overridePrompt) =>
                  overridePrompt.cartItemIds.includes(cartItemValuePair.cartItemId)
                )

                if (!overridePrompt || !overridePrompt.value) {
                  return null
                }

                return getPromptUrl(
                  itemCustomization.multiItemCustomization?.promptName || '',
                  cartItemValuePair.promptType,
                  overridePrompt.value
                )
              }
            ) || []
          )
        }
        return []
      })
      .filter((itemCustomization) => itemCustomization !== null)
      .join('&') + (canvasId ? `&canvasId=${encodeURIComponent(canvasId)}` : '')

  return `${import.meta.env.VITE_SUNNY_16_RENDER_API_BASE_URL}${cartItem.item?.canvasLayout.assetName}?${promptsQuerystring}`
}

const getImagePrompts = (
  customizationPrompts: PromptDto[] | null | undefined
): ImagePromptDto[] => {
  const prompts = customizationPrompts?.filter((prompt) => promptIsImagePrompt(prompt))
  return sortBy(prompts, 'zOrder') as ImagePromptDto[]
}

const getTextPrompts = (customizationPrompts: PromptDto[] | null | undefined): TextPromptDto[] => {
  const prompts = customizationPrompts?.filter((prompt) => promptIsTextPrompt(prompt))
  return sortBy(prompts, 'zOrder') as TextPromptDto[]
}

const getDropdownPrompts = (
  customizationPrompts: PromptDto[] | null | undefined
): DropdownPromptDto[] => {
  const prompts = customizationPrompts?.filter((prompt) => promptIsDropdownPrompt(prompt))
  return sortBy(prompts, 'zOrder') as DropdownPromptDto[]
}

const promptIsImagePrompt = (prompt: PromptDto): boolean => {
  return 'supportedAspectRatios' in prompt
}

const promptIsTextPrompt = (prompt: PromptDto): boolean => {
  return 'placeHolderText' in prompt
}

const promptIsCheckboxListPrompt = (prompt: PromptDto): boolean => {
  return 'checkBoxListItems' in prompt
}

const promptIsDropdownPrompt = (prompt: PromptDto): boolean => {
  return 'dropdownItems' in prompt
}

const promptResponseIsStringPromptResponse = (
  promptResponse: Partial<PromptResponseDto> | Partial<ImagePromptResponseDto>
): boolean => {
  return typeof promptResponse.value === 'string'
}

const promptResponseIsImagePromptResponse = (
  promptResponse: Partial<PromptResponseDto> | Partial<ImagePromptResponseDto>
): boolean => {
  return typeof promptResponse.value === 'object' && 'canonicalUrl' in promptResponse.value
}

const getPromptDefaultValue = (
  prompt: PromptDto,
  personalizationMap: PersonalizationMap
): string | null => {
  if (promptIsTextPrompt(prompt)) {
    const textPrompt = prompt as TextPromptDto
    if (textPrompt.templateText && textPrompt.templateText.includes('##')) {
      return getTextPersonalized(textPrompt.templateText, personalizationMap)
    } else if (textPrompt.templateText) {
      return textPrompt.templateText
    } else {
      return null
    }
  } else if (promptIsDropdownPrompt(prompt)) {
    const dropdownPrompt = prompt as DropdownPromptDto
    let defaultValue: string = dropdownPrompt.defaultValue

    // If the default value contains '##', we need to personalize it
    if (defaultValue.includes('##')) {
      defaultValue = getTextPersonalized(dropdownPrompt.defaultValue, personalizationMap)
    }

    // If the default value is in the dropdown items, return it
    if (dropdownPrompt.dropdownItems.some((item) => item.value === defaultValue)) {
      return defaultValue
    }

    // If no valid default value, return the first dropdown item value or null
    return dropdownPrompt.dropdownItems[0]?.value ?? null
  } else {
    return null
  }
}

const isPromptRequired = (prompt: PromptDto): boolean => {
  return prompt.required
}

export {
  constructCartBundleItemPreviewUrls,
  constructLivePreviewUrl,
  getDropdownPrompts,
  getImagePrompts,
  getPromptDefaultValue,
  getTextPrompts,
  isPromptRequired,
  promptIsCheckboxListPrompt,
  promptIsDropdownPrompt,
  promptIsImagePrompt,
  promptIsTextPrompt,
  promptResponseIsImagePromptResponse,
  promptResponseIsStringPromptResponse
}

// // UNUSED - kept for potential future use
// function someItemCustomizationsHaveValues(itemCustomizations: CartItemCustomization[]) {
//   return itemCustomizations.some((itemCustomization) => {
//     if (itemCustomization.isSingleItemCustomization()) {
//       return itemCustomization.singleItemCustomization?.somePromptValuePairsAreNotNull()
//     } else {
//       return itemCustomization.multiItemCustomization?.someCartItemValuePairsAreNotNull()
//     }
//   })
// }

function getPromptTextUrl(
  promptObject: PromptValuePair | Partial<PromptResponseDto> | Partial<ImagePromptResponseDto>
) {
  return `${promptObject.promptName}=${encodeURIComponent((promptObject.value as string) ?? '')}`
}

function getPromptImageUrl(
  promptObject: PromptValuePair | Partial<PromptResponseDto> | Partial<ImagePromptResponseDto>
) {
  const imagePromptValue = promptObject.value as MediaReferenceDto

  let imagePromptUrl = `${promptObject.promptName}.imageUrl=${encodeURIComponent(imagePromptValue.thumbUrl)}`

  if (imagePromptValue.attributes && imagePromptValue.attributes.length > 0) {
    imagePromptUrl += `&${promptObject.promptName}.attributes=${encodeURIComponent(imagePromptValue.attributes)}`
  }

  return imagePromptUrl
}
